import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Logger from '@medflex/utils/Logger';
import InputField from '@medflex/components/stories/InputField';
import Button from '@medflex/components/stories/Button';
import DropDown from '@medflex/components/stories/DropDown';
import Spinner from '@medflex/components/stories/Spinner';
import ProfileItems, { ProfileItem, SubTitle } from '@medflex/components/stories/ProfileItems';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { IconLockClosed } from '@medflex/components/stories/Icons';
import { trackEvent } from '@medflex/utils/Tracking';
import PhoneNumber from '@medflex/components/stories/PhoneNumber';
import commons from '@medflex/commons';
import { isMobilePhoneNumber } from '@medflex/shared-utils';
import FormErrorMessage from '@medflex/components/stories/FormErrorMessage';
import useUserProfile from '../hooks/useUserProfile';
import useModal from '../hooks/useModal';

const NoSelectionOption = ({ children }) => {
  const { t } = useTranslation('physician');
  return (
    <option value="none">
      {children || t('PhysicianPersonalProfileEditModule.form.no-selection')}
    </option>
  );
};

NoSelectionOption.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

const PhysicianPersonalProfileEditModule = () => {
  const { close } = useModal();
  const dispatch = useDispatch();
  const { userProfile, update, isUpdating } = useUserProfile();

  const { t } = useTranslation('physician');
  const userProfileErrors = useSelector(e => e.errors.userProfile);
  const { gender, title, firstName, lastName, email, mobileNumber, phoneNumber } =
    userProfile.attributes;

  const validationSchema = yup.object().shape({
    gender: yup.string().required(),
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    mobileNumber: yup
      .string()
      .test(
        'has valid format',
        t('global:validation.messages.mobileNumber'),
        value => isMobilePhoneNumber(value) || value.length === 0,
      )
      .label('Mobilnummer'),
    phoneNumber: yup
      .string()
      .test(
        'has valid format',
        t('global:validation.messages.phoneNumber'),
        value => commons.phoneNumber.transformableToE164(value) || value.length === 0,
      )
      .label('Festnetz'),
  });

  const {
    register: registerFormElement,
    handleSubmit: wrapSubmit,
    formState,
    control,
    errors: formErrors,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const showGenericError = () => {
    showAlertToast(t('PhysicianPersonalProfileEditModule.unexpected_error.title'), null, {
      toastId: 'profileEditFailed',
    });
  };

  const isSubmitDisabled = !formState.isValid || isUpdating;

  useEffect(() => {
    if (userProfileErrors) {
      Logger.error(`PhysicianPersonalProfileEditModule: ${JSON.stringify(userProfileErrors)}`);
      showGenericError();
      dispatch.errors.clear();
    }
  }, [userProfileErrors]);

  const handleSubmit = async (formData, event) => {
    const netData = Object.entries(formData).reduce((memo, [key, value]) => {
      if (key === 'mobileNumber' && !formData.mobileNumber) return { ...memo, [key]: null };
      if (value !== 'none') return { ...memo, [key]: value };
      return { ...memo, [key]: null };
    }, {});

    try {
      await update(netData);
      trackEvent(event, { actionText: 'edit' });
      close({ modal: 'profile_personal' });
      showSuccessToast(t('PhysicianPersonalProfileEditModule.success.title'), null, {
        toastId: 'physicianEditSuccess',
      });
    } catch (e) {
      e.message = `PhysicianPersonalProfileEditModule#handleSubmit(): ${e.message}`;
      Logger.error(e);
      showGenericError();
    }
  };

  const handleClose = e => {
    e.preventDefault();
    close({ modal: 'profile_personal' });
  };

  return (
    <Modal
      showCloseButton
      className="min-h-0 flex flex-col"
      onClickOutside={handleClose}
      onClickCloseButton={handleClose}
      title={t('PhysicianPersonalProfileEditModule.title')}
    >
      <div data-component="PhysicianPersonalProfileEditModule">
        <form
          onSubmit={wrapSubmit(handleSubmit)}
          className="flex flex-row flex-wrap items-start lg:justify-between"
        >
          <ProfileItems>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.salutation')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <DropDown
                name="gender"
                ref={registerFormElement}
                options={Object.keys(t('global:gender', { returnObjects: true }))}
                optionLabels={Object.values(t('global:gender', { returnObjects: true }))}
                autoComplete="sex"
                defaultValue={gender}
              />
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.title')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <DropDown
                name="title"
                ref={registerFormElement}
                autoComplete="honorific-prefix"
                defaultValue={title}
              >
                <NoSelectionOption>
                  {t('PhysicianPersonalProfileEditModule.form.no-title')}
                </NoSelectionOption>
                {Object.values(t('global:title', { returnObjects: true })).map(value => (
                  <option key={`title#${value}`}>{value}</option>
                ))}
              </DropDown>
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.firstName')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="firstName"
                autoComplete="given-name"
                defaultValue={firstName}
              />
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.lastName')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="lastName"
                autoComplete="family-name"
                defaultValue={lastName}
              />
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.mobileNumber.label')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <PhoneNumber
                name="mobileNumber"
                control={control}
                defaultValue={mobileNumber || ''}
                placeholder={t('PhysicianPersonalProfileEditModule.form.mobileNumber.placeholder')}
                localStorageWriteEnabled
              />
              {formErrors.mobileNumber && (
                <FormErrorMessage className="mt-1" message={formErrors.mobileNumber.message} />
              )}
              <SubTitle Icon={IconLockClosed}>
                {t('PhysicianPersonalProfileEditModule.form.mobileNumber.subtitle')}
              </SubTitle>
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.phoneNumber.label')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <PhoneNumber
                name="phoneNumber"
                control={control}
                defaultValue={phoneNumber || ''}
                placeholder={t('PhysicianPersonalProfileEditModule.form.phoneNumber.placeholder')}
                localStorageWriteEnabled
                type="landline"
              />
              {formErrors.phoneNumber && (
                <FormErrorMessage className="mt-1" message={formErrors.phoneNumber.message} />
              )}
              <SubTitle Icon={IconLockClosed}>
                {t('PhysicianPersonalProfileEditModule.form.phoneNumber.subtitle')}
              </SubTitle>
            </ProfileItem>
            <ProfileItem
              label={t('PhysicianPersonalProfileEditModule.form.email.label')}
              className="lg:even:pl-4 lg:w-1/2"
            >
              <InputField
                ref={registerFormElement}
                name="email"
                type="email"
                readOnly
                inputClassName="cursor-not-allowed pointer-events-none"
                autoComplete="email"
                defaultValue={email}
              />
              <SubTitle Icon={IconLockClosed}>
                {t('PhysicianPersonalProfileEditModule.form.email.subtitle')}
              </SubTitle>
            </ProfileItem>
          </ProfileItems>
          <div className="flex-auto w-full flex justify-center py-2">
            <Button type="submit" isDisabled={isSubmitDisabled}>
              {isUpdating ? <Spinner /> : t('PhysicianPersonalProfileEditModule.form.save')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PhysicianPersonalProfileEditModule;
